import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCredentials, selectLoadingCredentialIDs } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DeleteCredentialsModal = ({ children, credentialIDs = [], visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Redux States
	const credentials = useSelector(selectCredentials);
	const loadingCredentialIDs = useSelector(selectLoadingCredentialIDs);
	const loading = Object.keys(loadingCredentialIDs).length > 0;

	// ✅ Local State
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Filter selected credentials
	const filteredCredentials = useMemo(
		() => credentials.filter((c) => credentialIDs.includes(c.credentialID)),
		[credentials, credentialIDs]
	);

	// ✅ Generate selected credential names
	const selectedNames = useMemo(
		() => filteredCredentials.map((cred) => cred.name || `#${cred.credentialID}`),
		[filteredCredentials]
	);

	// ✅ Generate dynamic messages
	const generateDeleteTitle = () =>
		credentialIDs.length > 1
			? `Are you sure you want to delete these ${credentialIDs.length} credentials?`
			: 'Are you sure you want to delete this credential?';

	const generateDeleteButtonText = () => (credentialIDs.length > 1 ? 'Delete Credentials' : 'Delete');

	// ✅ Close modal
	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// ✅ Handle deletion
	const handleDelete = () => {
		for (const credentialID of credentialIDs) {
			dispatch(credentialsActions.deleteCredential({ credentialID }));
		}
		setTimeout(() => {
			handleCancel();
		}, 500);
	};

	return (
		<>
			<Tooltip title={'Please contact support to delete a credential.'} placement='top'>
				<span onClick={(e) => setVisible(true)}>
					{children ? (
						children
					) : (
						<Button
							type='text'
							size='small'
							icon={<DeletedFilled color='currentColor' width={16} height={16} />}
							disabled={true}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loading} block>
							Cancel
						</Button>
						<Button onClick={handleDelete} loading={loading} disabled={loading} type='primary' danger block>
							{generateDeleteButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{generateDeleteTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action is permanent and <br /> cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>
									{credentialIDs.length > 1 ? 'Selected Credentials' : 'Selected Credential'}
								</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DeleteCredentialsModal;
